import React, { useEffect, useState, useRef } from "react";
import { getHoldings, getAccounts } from "../../api/plaidClient";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import LoadingSpinner from "../ui/LoadingSpinner";
import { formatCurrency, formatPercent, formatGainLossPercent } from "../../utils/formatters";

function isValidTicker(ticker) {
    return typeof ticker === "string" && /^[A-Z0-9]{1,5}$/.test(ticker);
}

// Colors for the allocation chart
const CHART_COLORS = [
    "#2563EB", // blue
    "#0EA5E9", // sky
    "#6366F1", // indigo
    "#8B5CF6", // violet
    "#EC4899", // pink
    "#F97316", // orange
    "#F59E0B", // amber
    "#10B981", // emerald
    "#06B6D4", // cyan
    "#84CC16", // lime
    "#64748B", // slate (for "Other")
];

function aggregateHoldings(holdings) {
    const aggregated = {};

    for (const h of holdings) {
        // Skip cash holdings
        if (h.name === "U S Dollar") continue;

        const key = h.tickerSymbol || h.securityId;

        if (!aggregated[key]) {
            aggregated[key] = {
                tickerSymbol: h.tickerSymbol,
                name: h.name,
                securityId: h.securityId,
                quantity: 0,
                costBasis: 0,
                marketValue: 0,
                gainLoss: 0,
                accounts: [],
            };
        }

        aggregated[key].quantity += Number(h.quantity) || 0;
        aggregated[key].costBasis += Number(h.costBasis) || 0;
        aggregated[key].marketValue += Number(h.marketValue) || 0;
        aggregated[key].gainLoss += Number(h.gainLoss) || 0;

        // Track which accounts hold this
        if (!aggregated[key].accounts.includes(h.accountId)) {
            aggregated[key].accounts.push(h.accountId);
        }
    }

    // Calculate derived fields and convert to array
    return Object.values(aggregated).map(h => ({
        ...h,
        costShare: h.quantity > 0 ? h.costBasis / h.quantity : 0,
        closePrice: h.quantity > 0 ? h.marketValue / h.quantity : 0,
        accountCount: h.accounts.length,
    }));
}

function CustomTooltip({ active, payload }) {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md">
                <p className="text-gray-900 font-semibold">{data.ticker}</p>
                <p className="text-gray-500 text-xs">{data.name}</p>
                <p className="text-gray-900 font-semibold mt-1">{formatCurrency(data.value)}</p>
                <p className="text-gray-500 text-xs">{formatPercent(data.percentage, { decimals: 1 })} of portfolio</p>
            </div>
        );
    }
    return null;
}

const INVESTMENT_TYPES = new Set(["investment", "brokerage"]);

function formatSubtype(subtype) {
    if (!subtype) return "Investment";
    return subtype.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export default function InvestmentsPage({ holdings: propHoldings }) {
    const [holdings, setHoldings] = useState(propHoldings || []);
    const [balanceOnlyAccounts, setBalanceOnlyAccounts] = useState([]);
    const [loading, setLoading] = useState(!propHoldings);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "marketValue", direction: "desc" });
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (propHoldings) {
            setHoldings(propHoldings);
            setLoading(false);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        async function fetchData() {
            try {
                const [holdingsData, accountsData] = await Promise.all([
                    getHoldings(signal),
                    getAccounts(true, signal),
                ]);
                setHoldings(holdingsData);

                // Find investment/brokerage accounts with no holdings data
                const holdingsAccountIds = new Set(holdingsData.map(h => h.accountId));
                const noHoldingsAccounts = accountsData.filter(
                    a => INVESTMENT_TYPES.has(a.type?.toLowerCase()) && !holdingsAccountIds.has(a.accountId)
                );
                setBalanceOnlyAccounts(noHoldingsAccounts);
            } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError') {
                    return;
                }
                console.error("Error fetching holdings:", err);
                setError("Failed to load investment holdings");
            } finally {
                setLoading(false);
            }
        }
        fetchData();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [propHoldings]);

    // Aggregate holdings by ticker
    const aggregatedHoldings = aggregateHoldings(holdings);

    // Calculate balance-only total
    const balanceOnlyTotal = balanceOnlyAccounts.reduce(
        (sum, a) => sum + (a.balances?.current || 0), 0
    );

    // Calculate total portfolio value (holdings + balance-only accounts)
    const holdingsValue = aggregatedHoldings.reduce((sum, h) => sum + h.marketValue, 0);
    const totalPortfolioValue = holdingsValue + balanceOnlyTotal;
    const totalCostBasis = aggregatedHoldings.reduce((sum, h) => sum + h.costBasis, 0);
    const totalGainLoss = aggregatedHoldings.reduce((sum, h) => sum + h.gainLoss, 0);
    const totalGainLossPercent = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0;

    // Add percentage to each holding
    const holdingsWithPercent = aggregatedHoldings.map(h => ({
        ...h,
        percentOfPortfolio: totalPortfolioValue > 0 ? (h.marketValue / totalPortfolioValue) * 100 : 0,
    }));

    // Sort holdings
    const sortedHoldings = [...holdingsWithPercent].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (sortConfig.direction === "asc") {
            return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
    });

    // Prepare chart data (top 10 + other)
    const sortedByValue = [...holdingsWithPercent].sort((a, b) => b.marketValue - a.marketValue);
    const top10 = sortedByValue.slice(0, 10);
    const otherValue = sortedByValue.slice(10).reduce((sum, h) => sum + h.marketValue, 0);
    const otherPercent = totalPortfolioValue > 0 ? (otherValue / totalPortfolioValue) * 100 : 0;

    const chartData = top10.map((h, i) => ({
        ticker: isValidTicker(h.tickerSymbol) ? h.tickerSymbol : (h.name || "Unknown"),
        name: h.name || "Unknown",
        value: h.marketValue,
        percentage: h.percentOfPortfolio,
        color: CHART_COLORS[i % CHART_COLORS.length],
    }));

    if (otherValue > 0) {
        chartData.push({
            ticker: "Other",
            name: `${sortedByValue.length - 10} other holdings`,
            value: otherValue,
            percentage: otherPercent,
            color: CHART_COLORS[10],
        });
    }

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
        }));
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <span className="text-gray-400 ml-1">↕</span>;
        }
        return <span className="text-accent-600 ml-1">{sortConfig.direction === "desc" ? "↓" : "↑"}</span>;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <p className="text-danger-500">{error}</p>
            </div>
        );
    }

    if (aggregatedHoldings.length === 0 && balanceOnlyAccounts.length === 0) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Investments</h1>
                    <p className="text-gray-500 mt-1">Your consolidated investment portfolio</p>
                </div>
                <div className="card p-8 text-center">
                    <p className="text-gray-600">No investment holdings found.</p>
                    <p className="text-gray-500 text-sm mt-2">Link an investment account to see your holdings here.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Investments</h1>
                <p className="text-gray-500 mt-1">Your consolidated investment portfolio</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card p-4">
                    <p className="text-sm text-gray-500">Total value</p>
                    <p className="text-2xl font-semibold text-gray-900 tracking-tight">{formatCurrency(totalPortfolioValue)}</p>
                </div>
                <div className="card p-4">
                    <p className="text-sm text-gray-500">Total cost basis</p>
                    <p className="text-2xl font-semibold text-gray-900 tracking-tight">{formatCurrency(totalCostBasis)}</p>
                </div>
                <div className="card p-4">
                    <p className="text-sm text-gray-500">Total gain/loss</p>
                    <p className={`text-2xl font-semibold tracking-tight ${totalGainLoss >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                        {formatCurrency(totalGainLoss)}
                        <span className="text-sm ml-2">({totalGainLossPercent >= 0 ? "+" : ""}{totalGainLossPercent.toFixed(2)}%)</span>
                    </p>
                </div>
            </div>

            {/* Chart and Holdings */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Allocation Chart */}
                <div className="card p-6">
                    <p className="text-sm font-medium text-gray-700 mb-4">Portfolio allocation</p>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    {/* Legend */}
                    <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                        {chartData.map((entry, index) => {
                            // For legend, use ticker if valid, otherwise truncate name
                            const legendLabel = isValidTicker(entry.ticker)
                                ? entry.ticker
                                : (entry.ticker.length > 20 ? entry.ticker.slice(0, 20) + "..." : entry.ticker);
                            return (
                                <div key={index} className="flex items-center justify-between text-sm gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div
                                            className="w-3 h-3 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: entry.color }}
                                        />
                                        <span className="text-gray-700 truncate" title={entry.name}>{legendLabel}</span>
                                    </div>
                                    <span className="text-gray-900 font-medium flex-shrink-0">{formatPercent(entry.percentage, { decimals: 1 })}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Holdings Table */}
                <div className="card p-6 lg:col-span-2">
                    <div className="flex justify-between items-center mb-4">
                        <p className="text-sm font-medium text-gray-700">
                            Holdings ({aggregatedHoldings.length} positions)
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th
                                        className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer hover:text-gray-900"
                                        onClick={() => handleSort("tickerSymbol")}
                                    >
                                        Ticker <SortIcon columnKey="tickerSymbol" />
                                    </th>
                                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 hidden md:table-cell">Name</th>
                                    <th
                                        className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer hover:text-gray-900"
                                        onClick={() => handleSort("marketValue")}
                                    >
                                        Value <SortIcon columnKey="marketValue" />
                                    </th>
                                    <th
                                        className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer hover:text-gray-900"
                                        onClick={() => handleSort("percentOfPortfolio")}
                                    >
                                        % <SortIcon columnKey="percentOfPortfolio" />
                                    </th>
                                    <th
                                        className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 cursor-pointer hover:text-gray-900 hidden sm:table-cell"
                                        onClick={() => handleSort("gainLoss")}
                                    >
                                        Gain/loss <SortIcon columnKey="gainLoss" />
                                    </th>
                                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 hidden lg:table-cell">Shares</th>
                                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 hidden xl:table-cell">Accounts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedHoldings.map((h) => {
                                    const hasValidTicker = isValidTicker(h.tickerSymbol);
                                    const gainLossPercentStr = formatGainLossPercent(h.gainLoss, h.costBasis);

                                    return (
                                        <tr key={h.securityId || h.tickerSymbol} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-3 text-gray-900 font-medium">
                                                {hasValidTicker ? h.tickerSymbol : "-"}
                                            </td>
                                            <td className="py-3 text-gray-600 hidden md:table-cell">
                                                <span className="block truncate max-w-[200px]" title={h.name}>
                                                    {h.name || "Unknown"}
                                                </span>
                                            </td>
                                            <td className="py-3 text-right text-gray-900 font-medium">
                                                {formatCurrency(h.marketValue)}
                                            </td>
                                            <td className="py-3 text-right text-gray-700">
                                                {formatPercent(h.percentOfPortfolio, { decimals: 1 })}
                                            </td>
                                            <td className={`py-3 text-right font-medium hidden sm:table-cell ${h.gainLoss >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                                                {formatCurrency(h.gainLoss)}
                                                {gainLossPercentStr && (
                                                    <span className="text-xs ml-1">({gainLossPercentStr})</span>
                                                )}
                                            </td>
                                            <td className="py-3 text-right text-gray-600 hidden lg:table-cell">
                                                {h.quantity.toFixed(2)}
                                            </td>
                                            <td className="py-3 text-right text-gray-500 hidden xl:table-cell">
                                                {h.accountCount > 1 ? `${h.accountCount} accounts` : "1 account"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Balance-only investment accounts (no holdings data from Plaid) */}
            {balanceOnlyAccounts.length > 0 && (
                <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Other investment accounts</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {balanceOnlyAccounts.map(account => (
                            <div key={account.accountId} className="card p-5">
                                <div className="flex justify-between items-start mb-1">
                                    <div className="min-w-0">
                                        <p className="text-gray-900 font-medium truncate">{account.name}</p>
                                        {account.officialName && account.officialName !== account.name && (
                                            <p className="text-gray-500 text-sm truncate">{account.officialName}</p>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0 ml-2">
                                        {formatSubtype(account.subtype)}
                                    </span>
                                </div>
                                <p className="text-2xl font-semibold text-gray-900 mt-3 tracking-tight">
                                    {formatCurrency(account.balances?.current || 0)}
                                </p>
                                <p className="text-gray-400 text-xs mt-2">
                                    Detailed holdings are not available for this institution.
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}